import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveLeague } from '../../App';
import { CampaignStatusBadge, CampaignTypeBadge } from './CampaignStatusBadge';
import { FaPlus } from 'react-icons/fa';

const STATUSES = ['draft', 'ready', 'sending', 'sent', 'failed', 'cancelled'];

function fetchCampaigns(leagueId, statusFilter) {
    const params = new URLSearchParams();
    if (leagueId) params.set('league', leagueId);
    if (statusFilter) params.set('status', statusFilter);
    return axios.get(`/api/campaigns/?${params}`, { withCredentials: true }).then((r) => r.data);
}

export default function CampaignListPage() {
    const navigate = useNavigate();
    const { activeLeague } = useActiveLeague();
    const [statusFilter, setStatusFilter] = useState('');

    const { data: campaigns = [], isLoading, error, refetch } = useQuery({
        queryKey: ['campaigns', activeLeague?.id, statusFilter],
        queryFn: () => fetchCampaigns(activeLeague?.id, statusFilter),
        staleTime: 30_000,
    });

    const totalSent = campaigns.filter((c) => c.status === 'sent').length;

    return (
        <div className="card">
            <div className="card-header border-0 pb-0">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h5 className="card-title mb-0">Campaigns</h5>
                        <p className="mb-0 text-muted small">
                            Email campaigns for {activeLeague?.name || 'your league'}.
                        </p>
                    </div>
                    <button
                        className="btn btn-primary btn-sm"
                        onClick={() => navigate('/league-manager/campaigns/new/')}
                    >
                        <FaPlus className="me-1" /> New campaign
                    </button>
                </div>
            </div>

            <div className="card-body">
                {/* Status filter tabs */}
                <ul className="nav nav-pills nav-pills-soft mb-3 gap-1 flex-wrap">
                    <li className="nav-item">
                        <button
                            className={`nav-link py-1 px-2 small ${statusFilter === '' ? 'active' : ''}`}
                            onClick={() => setStatusFilter('')}
                        >
                            All ({campaigns.length})
                        </button>
                    </li>
                    {STATUSES.map((s) => {
                        const count = campaigns.filter((c) => c.status === s).length;
                        if (count === 0 && s !== 'draft') return null;
                        return (
                            <li key={s} className="nav-item">
                                <button
                                    className={`nav-link py-1 px-2 small ${statusFilter === s ? 'active' : ''}`}
                                    onClick={() => setStatusFilter(s)}
                                >
                                    {s.charAt(0).toUpperCase() + s.slice(1)} ({count})
                                </button>
                            </li>
                        );
                    })}
                </ul>

                {isLoading && (
                    <div className="text-center py-4">
                        <div className="spinner-border spinner-border-sm" role="status" />
                    </div>
                )}

                {error && (
                    <div className="alert alert-danger small">Failed to load campaigns.</div>
                )}

                {!isLoading && campaigns.length === 0 && (
                    <div className="text-center py-5">
                        <p className="text-muted">No campaigns yet.</p>
                        <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => navigate('/league-manager/campaigns/new/')}
                        >
                            Create your first campaign
                        </button>
                    </div>
                )}

                {!isLoading && campaigns.length > 0 && (
                    <div className="table-responsive">
                        <table className="table table-hover align-middle">
                            <thead>
                                <tr>
                                    <th>Campaign</th>
                                    <th>Status</th>
                                    <th>Sent</th>
                                    <th>Delivered</th>
                                    <th>Opened</th>
                                    <th>Sent at</th>
                                </tr>
                            </thead>
                            <tbody>
                                {campaigns.map((c) => (
                                    <tr
                                        key={c.id}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/league-manager/campaigns/${c.id}/`)}
                                    >
                                        <td>
                                            <div className="fw-semibold">{c.title}</div>
                                            <div className="small text-muted">{c.subject || '—'}</div>
                                            <CampaignTypeBadge type={c.campaign_type} />
                                        </td>
                                        <td><CampaignStatusBadge status={c.status} /></td>
                                        <td>{c.recipient_sent}</td>
                                        <td>{c.recipient_delivered}</td>
                                        <td>{c.recipient_opened}</td>
                                        <td className="small">
                                            {c.sent_at ? new Date(c.sent_at).toLocaleDateString() : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
